import cx from 'classnames'
import TitleEmojiPicker from 'components/ActionBar/ArticleTitleBar/TitleEmojiPicker/TitleEmojiPicker'
import { DocEmoji } from 'components/DocEmoji/DocEmoji'
import { FolderEmoji } from './components/FolderEmoji/FolderEmoji'
import { useLayoutEffect, useRef, useState } from 'react'
import { AlmanacFile, FileType } from 'types'
import Styles from './CopyTitleInput.module.scss'
import FileIconStyles from 'screens/Finder/components/FileIcon/FileIcon.module.scss'

import { Item } from '../../CopyModal'
import { Studio } from 'almanac-ui/icons'
import { Video } from 'screens/Finder/components/views/GalleryView/icons/Video/Video'
import { Audio } from 'screens/Finder/components/views/GalleryView/icons/Audio/Audio'
import { DesignEmoji } from './components/DesignEmoji'

export interface CopyTitleInputProps {
  item: Item
  itemType?: FileType
  copyName: string
  copyEmoji: string | null
  onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void
  onEnter?: () => void
  onCopyEmojiChange: (emoji: string) => void
  onNameChange: (newName: string) => void
  file?: AlmanacFile
}

export const CopyTitleInput = ({
  item,
  itemType = 'article',
  copyName,
  copyEmoji,
  onNameChange,
  onCopyEmojiChange,
  onFocus,
  onEnter,
  file,
}: CopyTitleInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const hiddenInputRef = useRef<HTMLSpanElement>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false)
  const [inputFocused, setInputFocused] = useState<boolean>(false)

  const toggleShowEmojiPicker = () => setShowEmojiPicker(!showEmojiPicker)

  useLayoutEffect(() => {
    if (hiddenInputRef.current) {
      const width = hiddenInputRef.current.getBoundingClientRect().width + 15
      if (inputRef.current) inputRef.current.style.width = `${width}px`
    }
  }, [copyName, hiddenInputRef, inputRef])

  const fileIcon = () => {
    if (itemType === 'design') return <Studio />

    if (file?.mime_type?.startsWith('image')) {
      return <img alt="" className={FileIconStyles.mediaFile} src={file.source_url} />
    }
    if (file?.mime_type?.startsWith('video')) return <Video />
    if (file?.mime_type?.startsWith('audio')) return <Audio />
  }

  const fileEmoji = (emoji: string | null) => {
    if (emoji) return <FolderEmoji emoji={emoji} />

    return fileIcon()
  }

  return (
    <>
      <div
        className={cx(Styles.copyTitleInput, { [Styles.focused]: inputFocused })}
        onClick={() => inputRef?.current?.focus()}
      >
        <div
          className={Styles.articleIcon}
          onClick={(e) => {
            e.stopPropagation()
            toggleShowEmojiPicker()
          }}
        >
          {itemType === 'article' && <DocEmoji emoji={copyEmoji} />}
          {itemType === 'folder' && <FolderEmoji emoji={copyEmoji} />}
          {itemType === 'design' && <DesignEmoji emoji={copyEmoji} />}
          {itemType === 'media_file' && file && fileEmoji(copyEmoji)}
        </div>
        {showEmojiPicker && (
          <div className={Styles.emojiPicker}>
            <TitleEmojiPicker
              showEmojiPicker={showEmojiPicker}
              onSelect={(emoji) => {
                if (emoji) onCopyEmojiChange(emoji.native)
                toggleShowEmojiPicker()
              }}
              toggleShowEmojiPicker={toggleShowEmojiPicker}
            />
          </div>
        )}
        <input
          ref={inputRef}
          style={{
            width: `100%`,
          }}
          className={Styles.input}
          type="text"
          value={copyName}
          autoFocus
          onBlur={() => setInputFocused(false)}
          onFocus={(e) => {
            setInputFocused(true)
            onFocus?.(e)
          }}
          onChange={(e) => {
            onNameChange(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.code === 'Enter') onEnter?.()
          }}
        />
      </div>
      <span ref={hiddenInputRef} className={Styles.hiddenInput}>
        {copyName}
      </span>
    </>
  )
}
